import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import TurnstileWidget from '../../components/security/TurnstileWidget';
import { tenantSlugFromHost } from '../../utils/tenantDomains';
import ProfessionalAgentLanding from './ProfessionalAgentLanding';
import './AgentSignupPage.css';
import './SoAgentAuth.css';

const turnstileEnabled = ['1', 'true', 'yes', 'on'].includes(
  String(process.env.REACT_APP_TURNSTILE_ENABLED || '').trim().toLowerCase(),
);

export default function AgentSignupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const hostTenant = tenantSlugFromHost();
  const routeTenant = hostTenant || params.tenantSlug || '';
  const routePrefix = hostTenant ? '' : (params.tenantSlug ? `/${params.tenantSlug}` : '');
  const { user, login } = useAuth();
  const authOnly = /\/agent\/(?:signup|enroll|login|signin)\/?$/i.test(location.pathname)
    || new URLSearchParams(location.search).get('auth') === '1';
  const [busy, setBusy] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReset, setTurnstileReset] = useState(0);
  const [form, setForm] = useState({
    email: '',
    password: '',
  });

  useEffect(() => {
    if (user?.role === 'referral_agent' || user?.role === 'agent' || user?.role === 'affiliate_manager') {
      navigate(`${routePrefix}/agent-portal`, { replace: true });
    }
  }, [navigate, routePrefix, user]);

  const update = (key, value) => setForm((previous) => ({ ...previous, [key]: value }));

  const goPortal = () => navigate(`${routePrefix}/agent-portal`, { replace: true });

  const resetSecurityCheck = () => {
    setTurnstileToken('');
    setTurnstileReset((value) => value + 1);
  };

  const signIn = async () => {
    if (!form.email || !form.password) {
      toast.error('Email and password are required');
      return;
    }
    if (turnstileEnabled && !turnstileToken) {
      toast.error('Complete the security check');
      return;
    }
    setBusy(true);
    try {
      await login(form.email, form.password, routeTenant || null, 'agent', {
        turnstileToken: turnstileEnabled ? turnstileToken : undefined,
      });
      goPortal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sign in failed');
    } finally {
      setBusy(false);
      resetSecurityCheck();
    }
  };

  const submit = (event) => {
    event.preventDefault();
    signIn();
  };

  if (!authOnly) {
    return <ProfessionalAgentLanding search={location.search} />;
  }

  return (
    <main className="soagent-auth-page">
      <a className="soagent-auth-brand" href={`${routePrefix}/agent`} aria-label="SO Agent home">
        <img src="/assets/so-agents/brand-mark.png" alt="" />
        <span>SO Agent</span>
      </a>
      <section className="soagent-auth-shell" aria-labelledby="agentAuthTitle">
        <div className="soagent-auth-pane">
          <div className="soagent-auth-kicker">Partner access</div>
          <h1 id="agentAuthTitle">Log in.</h1>
          <p className="soagent-auth-intro">
            Log in to manage customers, reports, transactions, withdrawals, and support.
          </p>

          <form onSubmit={submit} noValidate>
            <label className="soagent-auth-field">
              <span>Email address</span>
              <input type="email" value={form.email} onChange={(event) => update('email', event.target.value)} autoComplete="email" placeholder="you@example.com" required />
            </label>

            <label className="soagent-auth-field">
              <span>Password</span>
              <span className="soagent-password-wrap">
                <input type={passwordVisible ? 'text' : 'password'} value={form.password} onChange={(event) => update('password', event.target.value)} autoComplete="current-password" placeholder="Your password" required />
                <button type="button" onClick={() => setPasswordVisible((visible) => !visible)} aria-label={passwordVisible ? 'Hide password' : 'Show password'}>
                  {passwordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </span>
            </label>

            {turnstileEnabled && (
              <TurnstileWidget
                action="agent_login"
                onToken={setTurnstileToken}
                resetSignal={turnstileReset}
              />
            )}

            <button className="soagent-auth-submit" type="submit" disabled={busy}>
              <span>{busy ? 'Please wait…' : 'Log in securely'}</span>
              <ArrowRight size={18} />
            </button>
          </form>

          <p className="soagent-auth-footnote">
            Agent accounts are created by an administrator. Contact your manager if you need access.
          </p>
        </div>
      </section>
    </main>
  );
}
